import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Stack,
  TextField,
  Button,
  Alert,
  Typography,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Grid,
  CircularProgress,
} from "@mui/material";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { MdSearch, MdMyLocation } from "react-icons/md";

import { Surface, SectionTitle } from "components/ui";
import { T } from "theme/tokens";
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LOADER_ID } from "utils/googleMaps";
import * as api from "../api";

// Falls back to central Hyderabad when a pharmacy has no coordinates yet, so
// the map opens somewhere useful rather than in the ocean at [0,0].
const FALLBACK = { lat: 17.4374837, lng: 78.5158477 };

const mapContainerStyle = { width: "100%", height: "420px", borderRadius: "12px" };

export default function LocationTab({ pharmacyId, pharmacy, onChanged, notify }) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const [position, setPosition] = useState(pharmacy?.coordinates || null);
  const [placeId, setPlaceId] = useState(pharmacy?.place_id || "");
  const [address, setAddress] = useState(pharmacy?.address || "");

  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const debounceRef = useRef();
  const mapRef = useRef(null);

  useEffect(() => {
    setPosition(pharmacy?.coordinates || null);
    setPlaceId(pharmacy?.place_id || "");
    setAddress(pharmacy?.address || "");
  }, [pharmacy]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    map.setCenter(FALLBACK);
    map.setZoom(11);
  }, []);

  // Drive the viewport imperatively so the map only moves when the PIN moves,
  // never because some unrelated state re-rendered the component.
  useEffect(() => {
    if (!mapRef.current || !position) return;
    mapRef.current.panTo(position);
    if (mapRef.current.getZoom() < 15) mapRef.current.setZoom(16);
  }, [position]);

  // Autocomplete is proxied through our backend so the Places key stays
  // server-side rather than shipping to every admin's browser.
  const search = useCallback((value) => {
    clearTimeout(debounceRef.current);
    if (!value || value.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.placesAutocomplete(value.trim());
        setSuggestions(res.predictions || []);
      } catch (err) {
        setError(err?.response?.data?.message || "Place search is unavailable.");
      } finally {
        setSearching(false);
      }
    }, 350);
  }, []);

  const choosePlace = async (suggestion) => {
    setError("");
    try {
      const res = await api.placeDetails(suggestion.place_id);
      setPosition({ lat: res.place.lat, lng: res.place.lng });
      setPlaceId(res.place.place_id);
      setAddress(res.place.address || suggestion.description);
      setSuggestions([]);
      setQuery(suggestion.description);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not resolve that place.");
    }
  };

  const save = async () => {
    if (!position) {
      setError("Pick a place or drag the pin first.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.updateLocation(pharmacyId, {
        lat: position.lat,
        lng: position.lng,
        place_id: placeId || undefined,
        address: address || undefined,
      });
      notify?.("Location saved.");
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not save the location.");
    } finally {
      setSaving(false);
    }
  };

  const dirty =
    position &&
    (position.lat !== pharmacy?.coordinates?.lat ||
      position.lng !== pharmacy?.coordinates?.lng ||
      address !== (pharmacy?.address || ""));

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {loadError && (
        <Alert severity="error">
          Google Maps failed to load. Check REACT_APP_GOOGLE_MAPS_API_KEY.
        </Alert>
      )}

      <Surface sx={{ p: { xs: 2, md: 3 } }}>
        <SectionTitle
          action={
            <Button
              size="small"
              variant="contained"
              onClick={save}
              disabled={!dirty || saving}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                bgcolor: T.primary,
                "&:hover": { bgcolor: "#013838" },
              }}
            >
              {saving ? "Saving…" : "Save location"}
            </Button>
          }
        >
          Find the pharmacy
        </SectionTitle>

        <Box sx={{ position: "relative", mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              search(e.target.value);
            }}
            placeholder="Search by pharmacy name or address…"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            InputProps={{
              startAdornment: <MdSearch color={T.subtle} style={{ marginRight: 8 }} />,
              endAdornment: searching ? <CircularProgress size={16} /> : null,
            }}
          />

          {suggestions.length > 0 && (
            <Surface
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                mt: 0.5,
                zIndex: 10,
                maxHeight: 280,
                overflowY: "auto",
              }}
            >
              <List dense disablePadding>
                {suggestions.map((suggestion) => (
                  <ListItemButton
                    key={suggestion.place_id}
                    onClick={() => choosePlace(suggestion)}
                  >
                    <ListItemText
                      primary={suggestion.mainText}
                      secondary={suggestion.secondaryText}
                      primaryTypographyProps={{ fontSize: "0.86rem", fontWeight: 600 }}
                      secondaryTypographyProps={{ fontSize: "0.75rem" }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Surface>
          )}
        </Box>

        {/* A pharmacy with no coordinates and a pharmacy whose map failed look
            identical otherwise — both are just an empty map. Say which. */}
        {isLoaded && !position && (
          <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
            No location saved for this pharmacy yet. Search for it above, or
            click the map to drop a pin.
          </Alert>
        )}

        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            // Uncontrolled: `center`/`zoom` as props would re-centre on every
            // render, so panning around would snap back the moment any other
            // state changed. The effect above pans imperatively instead.
            onLoad={onMapLoad}
            options={{ streetViewControl: false, mapTypeControl: false }}
            onClick={(e) =>
              setPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() })
            }
          >
            {position && (
              <Marker
                position={position}
                draggable
                onDragEnd={(e) =>
                  setPosition({ lat: e.latLng.lat(), lng: e.latLng.lng() })
                }
              />
            )}
          </GoogleMap>
        ) : (
          <Box
            sx={{
              height: 420,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 3,
              bgcolor: T.borderSoft,
            }}
          >
            <CircularProgress size={28} sx={{ color: T.primary }} />
          </Box>
        )}

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ mt: 1 }}
        >
          <Typography sx={{ fontSize: "0.75rem", color: T.subtle }}>
            <MdMyLocation style={{ verticalAlign: "-2px", marginRight: 4 }} />
            Search for the shop, or click the map / drag the pin to place it
            exactly. Nothing is saved until you press Save.
          </Typography>

          {position && (
            // Cross-check the pin against Google's own map before saving.
            <Button
              size="small"
              onClick={() =>
                window.open(
                  `https://maps.google.com/?q=${position.lat},${position.lng}`,
                  "_blank",
                  "noopener"
                )
              }
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                color: T.secondary,
                whiteSpace: "nowrap",
              }}
            >
              Verify on Google Maps
            </Button>
          )}
        </Stack>
      </Surface>

      <Surface sx={{ p: { xs: 2, md: 3 } }}>
        <SectionTitle>Address on record</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={2}
              label="Address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Latitude"
              value={position?.lat ?? ""}
              InputProps={{ readOnly: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Longitude"
              value={position?.lng ?? ""}
              InputProps={{ readOnly: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Google place ID"
              value={placeId}
              InputProps={{ readOnly: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
        </Grid>
      </Surface>
    </Stack>
  );
}
